import { useQuery } from "@apollo/client";
import { useLocation, useParams } from "react-router-dom";
import {
  GetMeetingUsingGoogleMeetUrlQueryQuery,
  GetMeetingUsingGoogleMeetUrlQueryQueryVariables,
} from "types/graphql-schema";

import Container from "@components/container/container";
import Error from "@components/error/error";
import GraphqlError from "@components/error/graphql-error";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";

import ExtensionMeetingContent from "./components/meeting-content";
import getMeetingUsingGoogleMeetUrlQuery from "./graphql/get-meeting-using-google-meet-url-query";

const ExtensionMeeting = ({ meetingCode }: { meetingCode?: string }) => {
  const params = useParams<{ meetingGroupId: string; meetingId: string }>();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const queryParamGoogleMeetUrl = urlParams.get("googleMeetUrl");
  const googleMeetUrl =
    queryParamGoogleMeetUrl !== null
      ? queryParamGoogleMeetUrl
      : meetingCode !== undefined
        ? `https://meet.google.com/${meetingCode}`
        : "";
  const meetingId = params.meetingId ? Number(params.meetingId) : undefined;

  const { loading, error, data } = useQuery<
    GetMeetingUsingGoogleMeetUrlQueryQuery,
    GetMeetingUsingGoogleMeetUrlQueryQueryVariables
  >(getMeetingUsingGoogleMeetUrlQuery, {
    fetchPolicy: "network-only",
    skip: !googleMeetUrl,
    variables: { googleMeetUrl },
  });
  const meeting = data?.meeting;

  if (meetingId) {
    return <ExtensionMeetingContent meetingId={meetingId} />;
  }
  if (meeting?.id) {
    return <ExtensionMeetingContent meetingId={meeting.id} />;
  }

  // Renders
  if (error) {
    return (
      <Container>
        <div className="mx-4">
          <GraphqlError
            error={error}
            whatDidNotWork="The meeting could not be loaded."
          />
        </div>
      </Container>
    );
  }

  if (!data && loading) {
    return (
      <Container>
        <Loading />
      </Container>
    );
  }

  return (
    <Container>
      <div className="mx-4">
        <Error title="No meetings associated with this Google Meet." />
      </div>
      <div className="mt-6 mx-4 flex justify-center">
        <AppLink
          to="/extension/meetings"
          className="text-blue-800 hover:underline"
        >
          View today's meetings
        </AppLink>
      </div>
    </Container>
  );
};

export default ExtensionMeeting;
